import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Action from "pages/_components/Action";
import Button from "pages/_components/Button";
import useSchema from "hooks/useSchema";
import * as Yup from "yup";
import { Col, Row } from "react-bootstrap";
import { isEmpty } from "lodash";
import { routerActions } from "connected-react-router";
import { useEcheckAccountSelector } from "pages/echeck/hooks/useEcheckAccouts";
import { DesktopButtons, MobileButtons, ResponsiveHeader } from "pages/_components/header";
import { Field, Form, Formik } from "formik";
import { actions as echeckActions } from "reducers/echecks";
import Notification from "pages/_components/Notification";
import FieldLabel from "pages/_components/fields/FieldLabel";
import Multilinefile from "pages/forms/_components/_fields/Multilinefile";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import { Link } from "react-router-dom";
import I18n from "pages/_components/I18n";
import * as i18n from "util/i18n";
import Image from "pages/_components/Image";
import GridTable from "pages/_components/GridTable/GridTable";
import MainContainer from "pages/_components/MainContainer";
import { isIOS } from "react-device-detect";
import AmountEchecks from "../components/AmountEchecks";

const FORM_ID = "echecks.massive";
const ARS = "ARS";
const CHECKING_ACCOUNT = "CC";

const MassiveEcheck = () => {
    const { multilineFile: processedFileData } = useSelector((state) => state.formFields);
    const [selectedAccountId, setSelectedAccountId] = useState("");
    const { stringRequired } = useSchema();
    const dispatch = useDispatch();
    const { accountOptions, fetching, accounts } = useEcheckAccountSelector(ARS, CHECKING_ACCOUNT);
    const placeholder = i18n.get("global.accountSelect");

    const regexFileNameFormat = {
        CH: /(ECHEQMASIVO)_\d{4}(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01])([0-5][0-9])([0-5][0-9])([0-5][0-9]).(xls|xlsx)/g,
    };

    const handleSubmit = (values) => {
        const completeDebitAccount = accounts.find((account) => account.productId === values.debitAccount);
        const massiveEcheckData = {
            ...values,
            accountInfo: {
                accountNumber: completeDebitAccount.accountNumber,
                accountType: completeDebitAccount.accountType,
                currency: completeDebitAccount.currency,
                shortLabel: completeDebitAccount.shortLabel,
            },
            processedFileData,
            amount: processedFileData.totalAmount,
            idForm: "LoadEcheqMassive",
        };
        dispatch(echeckActions.summaryMassiveEcheck(massiveEcheckData));
        dispatch(routerActions.push(`/massiveEchecks/confirm`));
    };

    const handleDownload = () => {
        dispatch(echeckActions.downloadTemplate());
    };

    const MassiveEcheckValidationSchema = Yup.lazy((values) =>
        Yup.object().shape({
            debitAccount: stringRequired,
            file: values.file
                ? Yup.array()
                      .of(Yup.object())
                      .min(1, i18n.get(`global.error.required`))
                : Yup.array().notRequired(),
        }),
    );

    return (
        <>
            <Notification scopeToShow="massiveEcheck" />
            <ResponsiveHeader id="massive.echeck.title" backLinkTo="/echecks">
                <DesktopButtons>
                    <Link className="btn btn-primary btn-block" to="/echecks/requestAmount">
                        <Image src="images/figma/icon/nuevo.svg" className="svg-icon" />
                        <I18n id="echecks.request" />
                    </Link>
                    <div className="toolbar-item  add-button" key="addMoreButtonsRight">
                        <Button
                            className="btn-outline"
                            image="images/figma/icon/download.svg"
                            label="massive.echeck.template"
                            onClick={handleDownload}
                        />
                    </div>
                </DesktopButtons>
                <MobileButtons
                    image={isIOS ? "images/figma/icon/menu-ios-white.svg" : "images/figma/icon/menu-ios.svg"}>
                    <Action labelKey="massive.echeck.template.mobile" handleClick={handleDownload} />
                    <Action labelKey="echecks.request" to="/echecks/requestAmount" />
                </MobileButtons>
            </ResponsiveHeader>
            <MainContainer showLoader={fetching}>
                <AmountEchecks selectedAccountId={selectedAccountId} />
                <Formik onSubmit={handleSubmit} validationSchema={MassiveEcheckValidationSchema}>
                    {({ values }) => {
                        const showFileUploader = values.debitAccount;
                        const canSubmit = !values.debitAccount || isEmpty(values.file);
                        return (
                            <>
                                <Form>
                                    <GridTable className="form">
                                        <GridTable.Container columns={12} columnsTemplate="fixed">
                                            <GridTable.Data columnStart={2} columnWidth={10}>
                                                <Field
                                                    component={ProductSelector}
                                                    idForm={FORM_ID}
                                                    name="debitAccount"
                                                    label={i18n.get("massive.echeck.debit.account")}
                                                    placeholder={placeholder}
                                                    data={{ emptyOptionLabel: "", options: accountOptions }}
                                                    onChange={(id) => setSelectedAccountId(id)}
                                                />
                                            </GridTable.Data>
                                            {showFileUploader && (
                                                <>
                                                    <GridTable.Data
                                                        columnStart={2}
                                                        columnWidth={10}
                                                        rowStart={2}
                                                        className="form-group">
                                                        <FieldLabel labelKey="massive.echeck.file" />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={2} columnWidth={10}>
                                                        <Field
                                                            name="file"
                                                            component={Multilinefile}
                                                            type="multilinefile"
                                                            mode="edit"
                                                            formVersion="1"
                                                            sub_type="LoadEcheqMassive"
                                                            formTitle="LoadEcheqMassive"
                                                            allowMultiple="false"
                                                            acceptedFileTypes={[
                                                                "application/vnd.ms-excel",
                                                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                                                            ]}
                                                            allowFileTypeValidation={false}
                                                            useForTotalAmount="true"
                                                            maxFileSizeMB="4"
                                                            idField="file"
                                                            idForm="LoadEcheqMassive"
                                                            notificationScope="massiveEcheck"
                                                            successNotificationMessage="forms.notification.uploadFile.success"
                                                            warningNotificationMessage="forms.inputFile.massiveEcheqs.warning"
                                                            linesInfoMessage="forms.inputFile.massiveEcheqs.success"
                                                            errorNotificationMessage="forms.inputFile.massiveEcheqs.error"
                                                            fileErrorMessage="forms.inputFile.massiveEcheqs.error"
                                                            description={values.debitAccount}
                                                            fileNameRegex={regexFileNameFormat.CH}
                                                            isRequired
                                                            idActivity="load.echeq.massive"
                                                            idAccount={values.debitAccount}
                                                        />
                                                    </GridTable.Data>
                                                </>
                                            )}
                                        </GridTable.Container>
                                    </GridTable>
                                    <Row className="form-actions">
                                        <Col xs={12} md={4} mdOffset={4}>
                                            <Button
                                                type="submit"
                                                className="btn-primary"
                                                label="global.continue"
                                                disabled={canSubmit}
                                            />
                                        </Col>
                                    </Row>
                                </Form>
                            </>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

export default MassiveEcheck;
